/**
 * useSiteZones - polls /v1/sites/{siteId}/proxy/api/zones at 1-minute cadence.
 *
 * Phase 9.1 (2026-04-29): single-purpose hook used by the CoverageMap on the
 * Site Detail Overview tab. We deliberately do NOT extend the consolidated
 * use-site-live-data hook; zones are not yet a "live" concept (they only
 * change when an admin reconfigures them in Phase 9.3) and the existing
 * hook is already at its polling-loop cap. A separate hook keeps coupling
 * minimal and lets Phase 9.x rate-limit zones independently.
 *
 * Cadence: 1 fetch per 60s, plus an immediate fetch on mount. AbortController
 * cancels any in-flight request on unmount or siteId change. We honor the
 * existing 60 req/min ceiling by capping at 1 fetch/min on this loop.
 *
 * Status semantics:
 *   - 'loading' until the first response (success or error) lands.
 *   - 'live'    when the most recent fetch succeeded.
 *   - 'error'   when the most recent fetch failed (network, non-2xx, etc.).
 *     `zones` retains the last successful payload so the UI can stay
 *     populated while the loop attempts to recover.
 */
import { useEffect, useRef, useState } from 'react';
import { env } from '@/lib/env';
import { authStorage } from '@/lib/auth-storage';
import type { CoverageMapZone } from './types';
import { coerceRangeM } from './zone-projection';

/** Per-loop fetch lifecycle status. */
export type SiteZonesStatus = 'loading' | 'live' | 'error';

/** Hook return shape. */
export interface UseSiteZonesResult {
  /** Most-recent successful zones payload; never null after first success. */
  zones: CoverageMapZone[];
  /** Current loop status. */
  status: SiteZonesStatus;
  /** ISO-string of the last successful fetch (debug / "as of" labels). */
  lastSuccessAt: string | null;
}

/**
 * Polling cadence in milliseconds. Locked at 1 fetch / minute to honor the
 * fleet-api 60 req/min per-token ceiling without contributing more than 1/60
 * of the budget. Phase 9.x can revisit if zones become live-mutable.
 */
export const ZONES_POLL_MS = 60_000;

/**
 * Build the proxy URL for the per-site zones endpoint. Mirrors the
 * `proxyUrl` helper in use-site-live-data.ts but inline to keep this hook
 * self-contained (no cross-module coupling for a single endpoint).
 */
function zonesUrl(siteId: string): string {
  return `${env.FLEET_API_URL}/v1/sites/${encodeURIComponent(siteId)}/proxy/api/zones`;
}

/**
 * Coerce a raw API zone row to the CoverageMapZone contract.
 *
 * The upstream `/api/zones` endpoint returns rows with at least:
 *   { id, name, color, antenna_type, antenna_bearing_deg,
 *     antenna_beamwidth_deg, sequence_order, purpose, ... }
 *
 * We map `antenna_type` -> `zone_type` and clamp nullable bearing /
 * beamwidth fields when the upstream stack omitted them (older Pi
 * versions). Returns `null` when the row is unusable (no id / no name).
 *
 * @param raw Untyped row from JSON.parse.
 * @returns Typed zone or null when the row is malformed.
 */
export function coerceZone(raw: unknown): CoverageMapZone | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // 2026-05-29 FIX: accept `zone_id` as well as `id`. The on-site
  // /api/zones serializer returns `zone_id` (NOT `id`), so the prior
  // `r.id`-only read produced Number(undefined)=NaN and dropped EVERY
  // zone -> "No zones configured" + no map overlay across the whole SPA
  // (Overview CoverageMap, Zones config tab, replay arcs, LivePlayer,
  // NowPage). `id` keeps priority for back-compat; `zone_id` is the
  // fallback. Verified against sigsentry-002 /api/zones (returns zone_id).
  const idSource = r.id ?? r.zone_id;
  const id = typeof idSource === 'number' ? idSource : Number(idSource);
  if (!Number.isFinite(id)) return null;
  const name = typeof r.name === 'string' ? r.name : '';
  if (name.length === 0) return null;
  // The DB schema uses `antenna_type`; some older rows used `zone_type`.
  // We accept either and prefer antenna_type.
  const antennaType = (typeof r.antenna_type === 'string' ? r.antenna_type : null) ??
    (typeof r.zone_type === 'string' ? r.zone_type : 'omni');
  const zoneType: 'omni' | 'directional' =
    antennaType === 'directional' ? 'directional' : 'omni';
  const color = typeof r.color === 'string' ? r.color : '#888888';
  const bearing =
    typeof r.antenna_bearing_deg === 'number' ? r.antenna_bearing_deg : null;
  const beamwidth =
    typeof r.antenna_beamwidth_deg === 'number'
      ? r.antenna_beamwidth_deg
      : null;
  const sequenceOrder =
    typeof r.sequence_order === 'number' ? r.sequence_order : 1;
  const boxId = typeof r.box_id === 'string' ? r.box_id : null;
  // Phase 14 Plan 14-03 / CFG-01: range_m for the satellite zone overlay.
  // On-site migration 047 (2026-05-20) added the column with DEFAULT 50.0
  // but the on-site /api/zones row serializer does not (yet) project the
  // field on the wire. CFG-02 (REMOTE) closes the upstream gap; until
  // then the cloud SPA applies the canonical FALLBACK_RANGE_M (50.0 m)
  // via coerceRangeM() in zone-projection.ts. Routing both consumer
  // sites (this hook + AlertReplayPage.satelliteZoneOverlay) through
  // ONE helper means removing the fallback later is a single-file
  // edit. CoverageMapZone.range_m is therefore narrowed to `number`
  // (not `number | null`); downstream code never sees null on this
  // path. Supersedes the Phase 13 / REP-10 inline fallback.
  const rawRangeM: number | null =
    typeof r.range_m === 'number' ? r.range_m : null;
  const rangeM: number = coerceRangeM(rawRangeM);
  return {
    id,
    box_id: boxId,
    name,
    color,
    zone_type: zoneType,
    antenna_bearing_deg: zoneType === 'omni' ? null : bearing,
    antenna_beamwidth_deg: zoneType === 'omni' ? null : beamwidth,
    sequence_order: sequenceOrder,
    range_m: rangeM,
  };
}

/**
 * Polls the per-site /api/zones endpoint and exposes a typed list of zones
 * for the CoverageMap.
 *
 * @param siteId Site UUID (or null/undefined to disable the loop).
 * @returns { zones, status, lastSuccessAt }
 */
export function useSiteZones(
  siteId: string | null | undefined,
): UseSiteZonesResult {
  const [zones, setZones] = useState<CoverageMapZone[]>([]);
  const [status, setStatus] = useState<SiteZonesStatus>('loading');
  const [lastSuccessAt, setLastSuccessAt] = useState<string | null>(null);

  /**
   * Stable ref so the polling effect can replace the latest controller
   * without re-running the effect (which would also reset the timer).
   */
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!siteId) {
      setStatus('loading');
      setZones([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Single fetch + reschedule. Reschedules on BOTH success and failure so
     * a transient network blip recovers without operator intervention.
     */
    const tick = async (): Promise<void> => {
      // Replace any outstanding controller so the previous fetch is
      // abandoned. Node fetch on jsdom does not always honor abort, but
      // the cancelled flag below makes the state writes a no-op anyway.
      controllerRef.current?.abort();
      const ctrl = new AbortController();
      controllerRef.current = ctrl;

      const tok = authStorage.getAccess();
      const headers: Record<string, string> = tok
        ? { Authorization: `Bearer ${tok}` }
        : {};

      try {
        const res = await fetch(zonesUrl(siteId), {
          headers,
          signal: ctrl.signal,
        });
        if (cancelled) return;
        if (!res.ok) {
          setStatus('error');
        } else {
          const body = (await res.json()) as unknown;
          if (cancelled) return;
          const arr = Array.isArray(body)
            ? body
            : Array.isArray((body as { zones?: unknown })?.zones)
              ? ((body as { zones: unknown[] }).zones)
              : [];
          const coerced: CoverageMapZone[] = [];
          for (const row of arr) {
            const z = coerceZone(row);
            if (z) coerced.push(z);
          }
          setZones(coerced);
          setStatus('live');
          setLastSuccessAt(new Date().toISOString());
        }
      } catch (err) {
        if (cancelled) return;
        // AbortError on unmount is not a real failure; the cancelled flag
        // already short-circuits state writes for that case.
        if ((err as Error)?.name === 'AbortError') return;
        setStatus('error');
      }

      if (!cancelled) {
        timer = setTimeout(tick, ZONES_POLL_MS);
      }
    };

    // Kick off immediately. setTimeout(0) gives React a chance to paint the
    // initial loading state before the fetch resolves.
    timer = setTimeout(tick, 0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [siteId]);

  return { zones, status, lastSuccessAt };
}
